import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  CreditCard, Loader2, CheckCircle, Shield,
  Building2, ExternalLink, AlertCircle, Unlink, RefreshCw
} from 'lucide-react';

export function PaymentTab() {
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentData, setPaymentData] = useState({});
  const [savingPayment, setSavingPayment] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleConnectBank = useCallback(async (isRefresh = false) => {
    setConnectLoading(true);
    try {
      const origin = window.location.origin;
      const returnUrl = `${origin}/settings?tab=payment&connect=return`;
      const refreshUrl = `${origin}/settings?tab=payment&connect=refresh`;
      const res = isRefresh
        ? await api.refreshConnectLink({ return_url: returnUrl, refresh_url: refreshUrl })
        : await api.connectBank({ return_url: returnUrl, refresh_url: refreshUrl });
      if (res.data.already_connected) {
        toast.success('Bank account already connected!');
        const statusRes = await api.getConnectStatus();
        setConnectStatus(statusRes.data);
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start bank connection');
    }
    setConnectLoading(false);
  }, []);

  useEffect(() => {
    const loadPaymentData = async () => {
      try {
        const [payRes, connectRes] = await Promise.all([
          api.getPaymentMethods().catch(() => ({ data: {} })),
          api.getConnectStatus().catch(() => ({ data: null })),
        ]);
        setPaymentMethod(payRes.data.preferred_method || '');
        setPaymentData(payRes.data);
        setConnectStatus(connectRes.data);
      } catch (e) { toast.error('Failed to load payment settings'); }
    };
    loadPaymentData();

    // Handle Stripe Connect return
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') === 'return') {
      api.getConnectStatus().then(res => {
        setConnectStatus(res.data);
        if (res.data.connected) toast.success('Bank account connected successfully!');
        else if (res.data.details_submitted) toast.info('Your account is being verified. This usually takes a few minutes.');
        else toast.info('Onboarding incomplete. Please complete the setup to connect your bank.');
      }).catch(() => {});
      window.history.replaceState({}, '', '/settings?tab=payment');
    }
    if (params.get('connect') === 'refresh') {
      handleConnectBank(true);
    }
  }, [handleConnectBank]);

  const handleDisconnectBank = async () => {
    setDisconnecting(true);
    try {
      await api.disconnectBank();
      setConnectStatus({ connected: false, status: 'not_started' });
      toast.success('Bank account disconnected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
    setDisconnecting(false);
  };

  const handleSavePayment = async () => {
    if (!paymentMethod) { toast.error('Please select a payment method'); return; }
    setSavingPayment(true);
    try {
      await api.updatePaymentMethods({ preferred_method: paymentMethod, ...paymentData });
      toast.success('Payment method updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment method');
    }
    setSavingPayment(false);
  };

  return (
    <div className="space-y-6">
      {/* Stripe Connect */}
      <div className="bg-surface border border-[#262626] rounded-xl p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-1">Bank Account</h2>
          <p className="text-sm text-[#A1A1AA]">Connect your bank account for automatic withdrawals</p>
        </div>

        {connectStatus?.connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-lime/5 border border-lime/20 rounded-xl">
              <div className="w-12 h-12 bg-lime/10 rounded-xl flex items-center justify-center flex-shrink-0"><Building2 className="w-6 h-6 text-lime" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><p className="font-bold text-lime">Bank Connected</p><CheckCircle className="w-4 h-4 text-lime" /></div>
                <p className="text-sm text-[#A1A1AA]">Withdrawals will be sent directly to your bank account</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-lg border border-[#1a1a1a]">
              <Shield className="w-4 h-4 text-[#555]" />
              <p className="text-sm text-[#A1A1AA]">Your bank details are securely managed by Stripe. Tune4Tune never sees your account numbers.</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => handleConnectBank(true)} disabled={connectLoading} variant="outline" className="border-[#333]" data-testid="update-bank-btn">
                {connectLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}Update Bank Info
              </Button>
              <Button onClick={handleDisconnectBank} disabled={disconnecting} variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid="disconnect-bank-btn">
                {disconnecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlink className="w-4 h-4 mr-2" />}Disconnect
              </Button>
            </div>
          </div>
        ) : connectStatus?.status === 'pending' && connectStatus?.details_submitted ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-xl flex items-center justify-center flex-shrink-0"><AlertCircle className="w-6 h-6 text-yellow-500" /></div>
              <div><p className="font-bold text-yellow-400">Verification in Progress</p><p className="text-sm text-[#A1A1AA]">Stripe is reviewing your information. This usually takes a few minutes.</p></div>
            </div>
            <Button onClick={async () => { const res = await api.getConnectStatus(); setConnectStatus(res.data); if (res.data.connected) toast.success('Bank account is now active!'); else toast.info('Still verifying. Please check back shortly.'); }} variant="outline" className="border-[#333]" data-testid="check-status-btn">
              <RefreshCw className="w-4 h-4 mr-2" />Check Status
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-5 bg-[#0a0a0a] rounded-xl border border-[#1a1a1a]">
              <h3 className="font-semibold mb-3">How it works</h3>
              <ul className="text-sm text-[#A1A1AA] space-y-2.5">
                {['Click the button below to securely connect your bank', "Enter your bank details on Stripe's secure page", 'Withdrawals go directly to your bank — no delays'].map((text, i) => (
                  <li key={text} className="flex items-start gap-2">
                    <span className="w-5 h-5 bg-lime/10 text-lime rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                    {text}
                  </li>
                ))}
              </ul>
            </div>
            {connectStatus?.status === 'pending' && !connectStatus?.details_submitted && (
              <div className="flex items-center gap-3 p-3 bg-yellow-500/5 rounded-lg border border-yellow-500/20">
                <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <p className="text-sm text-yellow-400">You started the setup but didn't finish. Click below to continue.</p>
              </div>
            )}
            <Button onClick={() => handleConnectBank(connectStatus?.status === 'pending')} disabled={connectLoading} className="bg-lime text-black hover:bg-lime/90 font-bold" data-testid="connect-bank-btn">
              {connectLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Building2 className="w-4 h-4 mr-2" />}
              {connectStatus?.status === 'pending' ? 'Continue Setup' : 'Connect Bank Account'}
              <ExternalLink className="w-3 h-3 ml-2" />
            </Button>
            <p className="text-xs text-[#555]">Powered by Stripe. Your bank details are never shared with Tune4Tune.</p>
          </div>
        )}
      </div>

      {/* Manual Fallback */}
      <div className="bg-surface border border-[#262626] rounded-xl p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-1">Alternative Payout Methods</h2>
          <p className="text-sm text-[#A1A1AA]">For manual withdrawals processed by our team</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[{ id: 'paypal', label: 'PayPal' }, { id: 'zelle', label: 'Zelle' }, { id: 'bank_transfer', label: 'Bank Transfer' }].map((method) => (
            <button key={method.id} onClick={() => setPaymentMethod(method.id)} className={`p-4 rounded-xl border-2 transition-all text-center ${paymentMethod === method.id ? 'border-lime bg-lime/10 text-lime' : 'border-[#262626] hover:border-[#444] text-[#A1A1AA]'}`} data-testid={`payment-method-${method.id}`}>
              <CreditCard className={`w-6 h-6 mx-auto mb-2 ${paymentMethod === method.id ? 'text-lime' : 'text-[#555]'}`} />
              <span className="text-sm font-medium">{method.label}</span>
            </button>
          ))}
        </div>

        {paymentMethod && (
          <div className="space-y-4 max-w-md pt-2">
            {paymentMethod === 'paypal' && (
              <div>
                <Label htmlFor="paypalEmail" className="mb-2 block">PayPal Email</Label>
                <Input id="paypalEmail" type="email" value={paymentData.paypal_email || ''} onChange={(e) => setPaymentData({ ...paymentData, paypal_email: e.target.value })} placeholder="your@paypal.com" className="bg-[#111] border-[#262626]" data-testid="input-paypal-email" />
              </div>
            )}
            {paymentMethod === 'zelle' && (
              <>
                <div>
                  <Label htmlFor="zelleEmail" className="mb-2 block">Zelle Email</Label>
                  <Input id="zelleEmail" type="email" value={paymentData.zelle_email || ''} onChange={(e) => setPaymentData({ ...paymentData, zelle_email: e.target.value })} placeholder="your@email.com" className="bg-[#111] border-[#262626]" data-testid="input-zelle-email" />
                </div>
                <div>
                  <Label htmlFor="zellePhone" className="mb-2 block">Zelle Phone (optional)</Label>
                  <Input id="zellePhone" value={paymentData.zelle_phone || ''} onChange={(e) => setPaymentData({ ...paymentData, zelle_phone: e.target.value })} placeholder="+1 (555) 123-4567" className="bg-[#111] border-[#262626]" data-testid="input-zelle-phone" />
                </div>
              </>
            )}
            {paymentMethod === 'bank_transfer' && (
              <>
                <div><Label htmlFor="bankName" className="mb-2 block">Bank Name</Label><Input id="bankName" value={paymentData.bank_name || ''} onChange={(e) => setPaymentData({ ...paymentData, bank_name: e.target.value })} placeholder="e.g. Chase, Bank of America" className="bg-[#111] border-[#262626]" data-testid="input-bank-name" /></div>
                <div><Label htmlFor="bankAccName" className="mb-2 block">Account Holder Name</Label><Input id="bankAccName" value={paymentData.bank_account_name || ''} onChange={(e) => setPaymentData({ ...paymentData, bank_account_name: e.target.value })} placeholder="Full name on account" className="bg-[#111] border-[#262626]" data-testid="input-bank-account-name" /></div>
                <div><Label htmlFor="bankAccNum" className="mb-2 block">Account Number</Label><Input id="bankAccNum" value={paymentData.bank_account_number || ''} onChange={(e) => setPaymentData({ ...paymentData, bank_account_number: e.target.value })} placeholder="Account number" className="bg-[#111] border-[#262626]" data-testid="input-bank-account-number" /></div>
                <div><Label htmlFor="bankRouting" className="mb-2 block">Routing Number (optional)</Label><Input id="bankRouting" value={paymentData.bank_routing_number || ''} onChange={(e) => setPaymentData({ ...paymentData, bank_routing_number: e.target.value })} placeholder="Routing number" className="bg-[#111] border-[#262626]" data-testid="input-bank-routing" /></div>
              </>
            )}
          </div>
        )}

        {paymentMethod && (
          <Button onClick={handleSavePayment} disabled={savingPayment} className="bg-lime text-black hover:bg-lime/90 font-bold" data-testid="save-payment-btn">
            {savingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Save Payment Method
          </Button>
        )}
      </div>
    </div>
  );
}
